import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import {
  Box,
  Grid2,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  MenuItem,
  Avatar,
  Chip,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SearchIcon } from '../../../../assets/SVG/Search-Icon.svg';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../../components/alertProvider';
import { getRequest } from '../../../../services/httpService';
import { useAppQuery } from '../../../../services/appQuery';
import { RequiredLabel } from '../../../../utils/commonUtils';
import { formatDateDMY, todayISO } from '../../../../utils/dateUtils';

import {
  initialNewPatientValues,
  NewPatientCreatePayload,
  RegisterPatientAPIPayload,
  newPatientValidationSchema,
  useNewPatientMasters,
  registerPatientURL,
  useRegisterPatient,
  ExistingPatientsResponse,
  ExistingPatientDetail,
  getExistingPatient,
  RegistrationType,
  getDefaultDialCode,
} from './NewPharmacyPatientModal.helper';
import { modalStyles as sx } from './NewPharmacyPatientModal.styles';

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------
type Mode = 'existing' | 'new';

export interface NewPharmacyPatientModalProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called when the user closes or cancels the modal */
  onClose: () => void;
  /**
   * Called after:
   *  - Existing patient is selected ("Continue to Pharmacy Billing")
   *  - New patient is registered with "Save & Register (MRN)" (registration_type = "Registered")
   *  - New patient is saved with "Save as Pharmacy (Walk-in)" (registration_type = "Pharmacy")
   *
   * @param patientId  – the id returned from the API or selected existing patient
   * @param registrationType – how the patient was saved
   */
  onPatientSelected: (patientId: number, registrationType: RegistrationType | 'existing') => void;
  /** Clinic id – sent in the register payload and used for existing-patient search */
  clinicId: number | string;
  /** Dialog title override */
  title?: string;
}

// ---------------------------------------------------------------------
// Fallback data
// ---------------------------------------------------------------------
const fallbackCountryCodeOptions = [{ label: '+91 India', value: '+91' }];

// ---------------------------------------------------------------------
// Existing-patient detail card (read-only)
// ---------------------------------------------------------------------
interface PatientDetailCardProps {
  patient: ExistingPatientDetail;
  /** Pre-translated card labels */
  cardLabels: Record<string, string>;
}

const PatientDetailCard: React.FC<PatientDetailCardProps> = ({
  patient,
  cardLabels,
}) => {
  const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || '–';
  const initials = `${(patient.first_name || '')[0] || ''}${(patient.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <Box sx={sx.patientCard}>
      {/* Header with avatar, name & MRN chip */}
      <Stack direction="row" alignItems="center" spacing={2} sx={sx.cardHeader}>
        <Box
          sx={{
            ...sx.cardAvatarWrapper as object,
            ...(patient.photo ? sx.cardAvatarWrapperHover as object : {}),
          }}
        >
          <Avatar src={patient.photo || undefined} sx={sx.cardAvatar}>
            {initials}
          </Avatar>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={sx.cardName}>{fullName}</Typography>
          {patient.medical_record_no && (
            <Chip
              label={patient.medical_record_no}
              size="small"
              sx={sx.mrnChip}
            />
          )}
        </Box>
        {patient.gender_label && (
          <Typography sx={sx.genderText}>{patient.gender_label}</Typography>
        )}
      </Stack>

      {/* Detail grid */}
      <Box sx={sx.cardGrid}>
        <Box sx={sx.cardGridItem}>
          <Typography sx={sx.cardLabel}>{cardLabels.date_of_birth}</Typography>
          <Typography sx={sx.cardValue}>{patient.date_of_birth ? formatDateDMY(patient.date_of_birth) : '–'}</Typography>
        </Box>
        <Box sx={sx.cardGridItem}>
          <Typography sx={sx.cardLabel}>{cardLabels.mobile_number}</Typography>
          <Typography sx={sx.cardValue}>
            {patient.mobile_number_country_code ? `${patient.mobile_number_country_code} ` : ''}
            {patient.mobile_number || '-'}
          </Typography>
        </Box>
        <Box sx={sx.cardGridItem}>
          <Typography sx={sx.cardLabel}>{cardLabels.email}</Typography>
          <Typography sx={sx.cardValue}>{patient.email || '–'}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================================
// Main component
// =====================================================================
const NewPharmacyPatientModal: React.FC<NewPharmacyPatientModalProps> = ({
  open,
  onClose,
  onPatientSelected,
  clinicId,
  title,
}) => {
  const { t: tBilling } = useTranslation('billing_config');

  // ── Translated labels (memoised) ──
  const i18nPrefix = 'invoice.new_pharmacy_patient_modal';
  const modalLabels = useMemo(() => ({
    title: title ?? tBilling(`${i18nPrefix}.title`),
    modeExisting: tBilling(`${i18nPrefix}.mode_existing`),
    modeNew: tBilling(`${i18nPrefix}.mode_new`),
    searchPlaceholder: tBilling(`${i18nPrefix}.search_placeholder`),
    cancel: tBilling(`${i18nPrefix}.actions.cancel`),
    proceed: tBilling(`${i18nPrefix}.actions.proceed`),
    saveWalkIn: tBilling(`${i18nPrefix}.actions.save_pharmacy_invoice`),
    saveRegister: tBilling(`${i18nPrefix}.actions.save_register_patient`),
    saving: tBilling(`${i18nPrefix}.actions.saving`),
    registering: tBilling(`${i18nPrefix}.actions.registering`),
    registerSuccess: tBilling(`${i18nPrefix}.messages.register_success`),
    registerError: tBilling(`${i18nPrefix}.messages.register_error`),
    noPatientSelected: tBilling(`${i18nPrefix}.messages.no_patient_selected`),
    noPatientHint: tBilling(`${i18nPrefix}.messages.no_patient_hint`),
    ariaClose: tBilling(`${i18nPrefix}.aria.close`),
  }), [tBilling, title]);

  /** Labels for the read-only patient detail card */
  const cardLabels = useMemo(() => ({
    first_name: tBilling(`${i18nPrefix}.card_labels.first_name`),
    last_name: tBilling(`${i18nPrefix}.card_labels.last_name`),
    date_of_birth: tBilling(`${i18nPrefix}.card_labels.date_of_birth`),
    gender: tBilling(`${i18nPrefix}.card_labels.gender`),
    mrn: tBilling(`${i18nPrefix}.card_labels.mrn`),
    mobile_number: tBilling(`${i18nPrefix}.card_labels.mobile_number`),
    email: tBilling(`${i18nPrefix}.card_labels.email`),
  }), [tBilling]);

  /** Labels for the new patient form fields */
  const formLabels = useMemo(() => ({
    firstName: tBilling(`${i18nPrefix}.form_labels.first_name`),
    lastName: tBilling(`${i18nPrefix}.form_labels.last_name`),
    mobileNo: tBilling(`${i18nPrefix}.form_labels.mobile_no`),
    gender: tBilling(`${i18nPrefix}.form_labels.gender`),
    dateOfBirth: tBilling(`${i18nPrefix}.form_labels.date_of_birth`),
    email: tBilling(`${i18nPrefix}.form_labels.email`),
  }), [tBilling]);

  const formPlaceholders = useMemo(() => ({
    firstName: tBilling(`${i18nPrefix}.form_placeholders.first_name`),
    lastName: tBilling(`${i18nPrefix}.form_placeholders.last_name`),
    mobileNumber: tBilling(`${i18nPrefix}.form_placeholders.mobile_number`),
    gender: tBilling(`${i18nPrefix}.form_placeholders.gender`),
    dateOfBirth: tBilling(`${i18nPrefix}.form_placeholders.date_of_birth`),
    email: tBilling(`${i18nPrefix}.form_placeholders.email`),
  }), [tBilling]);

  // Compute initial values with default country code
  const computedInitialValues = useMemo(() => ({
    ...initialNewPatientValues,
    mobile_number_country_code: getDefaultDialCode(),
  }), []);

  // --- Local state ---
  const [mode, setMode] = useState<Mode>('existing');
  const [selectedPatient, setSelectedPatient] = useState<{
    label: string;
    value: number;
    detail?: ExistingPatientDetail;
  } | null>(null);
  const [selectedPatientDetail, setSelectedPatientDetail] =
    useState<ExistingPatientDetail | null>(null);

  // Ref to access Formik imperatively for the two-button New Patient flow
  const formikRef = useRef<FormikProps<NewPatientCreatePayload>>(null);

  const { genders, countryCodes } = useNewPatientMasters();
  const { addAlert } = useAlert();
  const registerPatientMutation = useRegisterPatient();

  // --- Existing-patient search ---
  const [searchValue, setSearchValue] = useState('');
  const {
    data: existingPatients,
    refetch: refetchPatients,
    isFetching,
  } = useAppQuery<ExistingPatientsResponse>(
    ['getExistingPatient', searchValue],
    () =>
      getRequest<ExistingPatientsResponse>(
        getExistingPatient(clinicId, searchValue)
      ),
    {
      staleTime: 0,
      debounceMs: 300,
      enabled: !!searchValue,
      refetchOnMount: true,
    }
  );

  // --- Memoised option lists ---
  const genderOptions = useMemo(
    () =>
      genders?.length
        ? genders.map((g) => ({ label: g.name_label, value: String(g.id) }))
        : [],
    [genders]
  );

  const countryCodeOptions = useMemo(
    () =>
      countryCodes?.length
        ? countryCodes.map((c) => ({
          label: `+${c.dial_code} ${c.name}`,
          value: `+${c.dial_code}`,
        }))
        : fallbackCountryCodeOptions,
    [countryCodes]
  );

  const existingPatientOptions = useMemo(
    () =>
      existingPatients?.objects?.map((p) => ({
        label: `${p.patient_name} (${p.medical_record_no})`,
        value: p.id,
        detail: {
          id: p.id,
          first_name: p.first_name ?? '',
          last_name: p.last_name ?? '',
          date_of_birth: p.date_of_birth ?? '',
          gender: p.gender_name ?? '',
          gender_label: p.gender_name ?? '',
          medical_record_no: p.medical_record_no ?? '',
          mobile_number: p.mobile_number ?? '',
          mobile_number_country_code: p.mobile_number_country_code ?? '',
          email: p.email ?? '',
          photo: p.photo ?? '',
        } as ExistingPatientDetail,
      })) ?? [],
    [existingPatients]
  );

  // --- Handlers ---
  const handleClose = useCallback(() => {
    setMode('existing');
    setSelectedPatient(null);
    setSelectedPatientDetail(null);
    setSearchValue('');
    onClose();
  }, [onClose]);

  /** Shared helper to call the register API */
  const registerPatient = useCallback(
    (values: NewPatientCreatePayload, regType: RegistrationType) => {
      // Look up gender name from genders list (API expects "MALE" or "FEMALE", not id)
      const genderName = genders?.find((g) => g.id === Number(values.gender))?.name ?? '';
      
      const body: RegisterPatientAPIPayload = {
        ...values,
        gender: genderName,
        clinic_id: clinicId,
        registration_type: regType,
        is_new: true,
      };

      registerPatientMutation.mutate(
        { path: registerPatientURL(), body },
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSuccess: (data: any) => {
            const id = data?.data?.id ?? data?.data?.patient_id;
            addAlert(
              data?.message ?? modalLabels.registerSuccess,
              'success'
            );
            handleClose();
            if (id) onPatientSelected(id, regType);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            addAlert(
              error?.message || modalLabels.registerError,
              'error'
            );
          },
        }
      );
    },
    [genders, registerPatientMutation, clinicId, addAlert, handleClose, onPatientSelected, modalLabels.registerSuccess, modalLabels.registerError]
  );

  /** Formik onSubmit — used only by "Save as Pharmacy (Walk-in)" */
  const handleFormikSubmit = useCallback(
    (values: NewPatientCreatePayload) => {
      registerPatient(values, 'Pharmacy');
    },
    [registerPatient]
  );

  /** "Save & Register (MRN)" for new patient – validates then calls API with "Registered"
   *  Temporarily commented out — button is hidden.
  const handleSaveAndRegisterNew = useCallback(async () => {
    if (!formikRef.current) return;
    const formik = formikRef.current;
    const errs = await formik.validateForm();
    formik.setTouched({
      first_name: true,
      last_name: true,
      mobile_number_country_code: true,
      mobile_number: true,
      gender: true,
      date_of_birth: true,
      email: true,
    });
    if (Object.keys(errs).length === 0) {
      registerPatient(formik.values, 'Registered');
    }
  }, [registerPatient]);
  */

  /** Existing patient — "Continue to Pharmacy Billing" */
  const handleContinueExisting = useCallback(() => {
    if (selectedPatient) {
      onPatientSelected(selectedPatient.value, 'existing');
      handleClose();
    }
  }, [selectedPatient, onPatientSelected, handleClose]);

  // =====================================================================
  // Render
  // =====================================================================
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      slotProps={{ paper: { sx: sx.paper } }}
    >
      {/* ---- Title ---- */}
      <DialogTitle sx={sx.titleBar}>
        <Box sx={sx.titleRow}>
          <Typography component="h2" sx={sx.titleText}>
            {modalLabels.title}
          </Typography>
          <IconButton
            aria-label={modalLabels.ariaClose}
            onClick={handleClose}
            sx={sx.closeBtn}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Formik<NewPatientCreatePayload>
        innerRef={formikRef}
        initialValues={computedInitialValues}
        validationSchema={newPatientValidationSchema}
        onSubmit={handleFormikSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          touched,
          errors,
        }) => (
          <Form>
            <DialogContent sx={sx.content}>
              {/* ---- Mode toggle ---- */}
              <RadioGroup
                row
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value as Mode);
                  setSelectedPatient(null);
                  setSelectedPatientDetail(null);
                }}
                sx={sx.modeRadioGroup}
              >
                <FormControlLabel
                  value="existing"
                  control={<Radio sx={sx.modeRadio} />}
                  label={
                    <Typography
                      sx={{ fontWeight: mode === 'existing' ? 600 : 500 }}
                    >
                      {modalLabels.modeExisting}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="new"
                  control={<Radio sx={sx.modeRadio} />}
                  label={
                    <Typography
                      sx={{ fontWeight: mode === 'new' ? 600 : 500 }}
                    >
                      {modalLabels.modeNew}
                    </Typography>
                  }
                />
              </RadioGroup>

              {/* ================================================================ */}
              {/* EXISTING PATIENT VIEW                                            */}
              {/* ================================================================ */}
              {mode === 'existing' ? (
                <Box>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={existingPatientOptions}
                    getOptionLabel={(opt) => opt.label}
                    loading={isFetching}
                    value={selectedPatient as typeof existingPatientOptions[number] | null}
                    onChange={(_, newValue) => {
                      setSelectedPatient(newValue);
                      const detail = (newValue as typeof existingPatientOptions[number] | null)?.detail ?? null;
                      setSelectedPatientDetail(detail);
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        setSearchValue(value);
                        if (value.length > 1) refetchPatients();
                      }
                    }}
                    renderInput={({ InputProps: inputWrapperProps, inputProps: htmlInputProps, ...params }) => (
                      <TextField
                        {...params}
                        placeholder={modalLabels.searchPlaceholder}
                        variant="outlined"
                        slotProps={{
                          input: {
                            ...inputWrapperProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon width={20} height={20} />
                              </InputAdornment>
                            ),
                          },
                          htmlInput: htmlInputProps,
                        }}
                      />
                    )}
                  />

                  {/* Patient detail card or empty state */}
                  {selectedPatientDetail ? (
                    <PatientDetailCard patient={selectedPatientDetail} cardLabels={cardLabels} />
                  ) : (
                    <Box sx={sx.emptyState}>
                      <Typography sx={sx.emptyStateTitle}>
                        {modalLabels.noPatientSelected}
                      </Typography>
                      <Typography sx={sx.emptyStateHint}>
                        {modalLabels.noPatientHint}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                /* ============================================================== */
                /* NEW PATIENT FORM                                               */
                /* ============================================================== */
                <Grid2 container direction="column" rowSpacing={2.5}>
                  {/* Row 1 – First Name | Last Name */}
                  <Grid2 container columnSpacing={2} rowSpacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        name="first_name"
                        label={RequiredLabel(formLabels.firstName, true)}
                        placeholder={formPlaceholders.firstName}
                        size="small"
                        variant="outlined"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          touched.first_name && errors.first_name
                        )}
                        helperText={touched.first_name && errors.first_name}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        name="last_name"
                        label={RequiredLabel(formLabels.lastName, true)}
                        placeholder={formPlaceholders.lastName}
                        size="small"
                        variant="outlined"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          touched.last_name && errors.last_name
                        )}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Grid2>
                  </Grid2>

                  {/* Row 2 – Mobile No. (country code + number) | Sex Assigned At Birth */}
                  <Grid2 container columnSpacing={2} rowSpacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        name="mobile_number"
                        label={RequiredLabel(formLabels.mobileNo, true)}
                        placeholder={formPlaceholders.mobileNumber}
                        size="small"
                        variant="outlined"
                        value={values.mobile_number}
                        onChange={(e) => {
                          // Allow only digits
                          const val = e.target.value.replace(/\D/g, '');
                          setFieldValue('mobile_number', val);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(
                          (touched.mobile_number && errors.mobile_number) ||
                          (touched.mobile_number_country_code && errors.mobile_number_country_code)
                        )}
                        helperText={
                          (touched.mobile_number && errors.mobile_number) ||
                          (touched.mobile_number_country_code && errors.mobile_number_country_code)
                        }
                        slotProps={{
                          htmlInput: { inputMode: 'numeric' as const, maxLength: 15 },
                          input: {
                            startAdornment: (
                              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                <Autocomplete
                                  size="small"
                                  disableClearable
                                  options={countryCodeOptions}
                                  getOptionLabel={(opt) => opt.value}
                                  renderOption={(props, opt) => (
                                    <li {...props} key={opt.value}>
                                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                                        {opt.label}
                                      </Typography>
                                    </li>
                                  )}
                                  value={
                                    countryCodeOptions.find(
                                      (opt) => opt.value === values.mobile_number_country_code
                                    ) ?? undefined
                                  }
                                  onChange={(_, newValue) => {
                                    setFieldValue(
                                      'mobile_number_country_code',
                                      newValue?.value || ''
                                    );
                                  }}
                                  filterOptions={(options, { inputValue }) => {
                                    const q = inputValue.toLowerCase();
                                    return options.filter(
                                      (opt) =>
                                        opt.value.includes(q) ||
                                        opt.label.toLowerCase().includes(q)
                                    );
                                  }}
                                  slotProps={{
                                    listbox: {
                                      style: { maxHeight: 300, overflow: 'auto' },
                                    },
                                    popper: {
                                      style: { width: 260 },
                                      placement: 'bottom-start',
                                    },
                                  }}
                                  sx={{
                                    width: 90,
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                    '& .MuiAutocomplete-input': {
                                      fontSize: 14,
                                      fontWeight: 500,
                                      p: '2px 4px !important',
                                      minWidth: '40px !important',
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      size="small"
                                    />
                                  )}
                                />
                                <Box sx={{ borderLeft: '1px solid #D1D5DB', height: 24, ml: 0.5 }} />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        select
                        name="gender"
                        label={RequiredLabel(formLabels.gender, true)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={values.gender ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.gender && errors.gender)}
                        helperText={touched.gender && errors.gender?.trim() ? errors.gender : undefined}
                        slotProps={{
                          inputLabel: { shrink: true },
                          select: { displayEmpty: true },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <Typography sx={{ color: '#9CA3AF', fontSize: 14 }}>
                            {formPlaceholders.gender}
                          </Typography>
                        </MenuItem>
                        {genderOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid2>
                  </Grid2>

                  {/* Row 3 – Date Of Birth | Email ID */}
                  <Grid2 container columnSpacing={2} rowSpacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        name="date_of_birth"
                        label={RequiredLabel(formLabels.dateOfBirth, true)}
                        placeholder={formPlaceholders.dateOfBirth}
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { max: todayISO() },
                        }}
                        size="small"
                        type="date"
                        variant="outlined"
                        value={values.date_of_birth}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.date_of_birth && errors.date_of_birth)}
                        helperText={
                          touched.date_of_birth && errors.date_of_birth?.trim() ? errors.date_of_birth : undefined
                        }
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        name="email"
                        label={RequiredLabel(formLabels.email, true)}
                        placeholder={formPlaceholders.email}
                        size="small"
                        variant="outlined"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email?.trim() ? errors.email : undefined}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>
              )}
            </DialogContent>

            {/* ============================================================== */}
            {/* ACTIONS                                                         */}
            {/* ============================================================== */}
            <DialogActions sx={sx.actionsBar}>
              <Box sx={sx.actionsBtnRow}>
                {/* Cancel – always shown */}
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  onClick={handleClose}
                  sx={sx.cancelBtn}
                >
                  {modalLabels.cancel}
                </Button>

                {mode === 'existing' ? (
                  /* Existing patient → Save & Register + Proceed */
                  <>
                    {/* Save & Register temporarily hidden
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={!selectedPatient}
                      sx={sx.saveRegisterBtn}
                    >
                      {modalLabels.saveRegister}
                    </Button>
                    */}
                    <Button
                      type="button"
                      variant="contained"
                      disableElevation
                      onClick={handleContinueExisting}
                      disabled={!selectedPatient}
                      sx={sx.proceedBtn}
                    >
                      {modalLabels.proceed}
                    </Button>
                  </>
                ) : (
                  /* New patient → two actions */
                  <>
                    {/* Save as Pharmacy (Walk-in) → registration_type = "Pharmacy" */}
                    <Button
                      type="submit"
                      variant="contained"
                      disableElevation
                      disabled={registerPatientMutation.isPending}
                      sx={sx.proceedBtn}
                    >
                      {registerPatientMutation.isPending
                        ? modalLabels.saving
                        : modalLabels.saveWalkIn}
                    </Button>

                    {/* Save & Register (MRN) → registration_type = "Registered" - temporarily hidden
                    <Button
                      type="button"
                      variant="contained"
                      disableElevation
                      disabled={registerPatientMutation.isPending}
                      onClick={handleSaveAndRegisterNew}
                      sx={sx.proceedBtn}
                    >
                      {registerPatientMutation.isPending
                        ? modalLabels.registering
                        : modalLabels.saveRegister}
                    </Button>
                    */}
                  </>
                )}
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default NewPharmacyPatientModal;